
import React, { useEffect, useState } from 'react';
import { Card, Row, Col, ProgressBar, Badge, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
  FaUsers, 
  FaBriefcase, 
  FaClipboardList, 
  FaFileAlt, 
  FaUserTie,
  FaCalendarAlt,
  FaArrowUp,
  FaArrowDown,
  FaClock,
  FaChartArea,
  FaSyncAlt,
  FaEllipsisV
} from 'react-icons/fa';
import axios from 'axios';

const Sparkline = ({ color }) => (
  <svg width="100%" height="40" className="mt-2">
    <path
      d={`M0 30 Q 20 ${Math.random() * 20 + 10} 40 ${Math.random() * 20 + 10} T 80 ${Math.random() * 20 + 5} T 120 ${Math.random() * 20 + 15}`}
      fill="none"
      stroke={color}
      strokeWidth="2"
    />
  </svg>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userStats, setUserStats] = useState({
    total_users: 0,
    total_jobs: 0,
    total_applications: 0,
    total_submissions: 0,
    total_expertise: 0,
  });

  const [summary, setSummary] = useState({
    newUsers: 0,
    jobApplications: 0,
    placementSubmissions: 0
  });
  
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get('https://api.collegeall.in/api/v1/dashboard-total');
      const totals = response.data.totals;
      setUserStats({
        total_users: totals.total_users || 0,
        total_jobs: totals.total_jobs || 0,
        total_applications: totals.total_applications || 0,
        total_submissions: totals.total_submissions || 0,
        total_expertise: totals.total_experts || 0,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchSummary = () => {
    axios.get('https://api.collegeall.in/api/v1/summary-today')
      .then(res => setSummary(res.data))
      .catch(err => console.error("Summary API error:", err));
  };

  const fetchActivities = () => {
    axios.get('https://api.collegeall.in/api/v1/recent-activity')
      .then(res => setActivities(res.data.activities || []))
      .catch(err => console.error("Recent activity API error:", err));
  };

  useEffect(() => {
    fetchData();
    fetchSummary();
    fetchActivities();
    const interval = setInterval(() => {
      fetchData();
      fetchSummary();
      fetchActivities();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const cards = [
    { title: 'Candidates', count: userStats.total_users, route: '/users', icon: <FaUsers />, color: '#6366f1', trend: '12%', up: true },
    { title: 'Open Jobs', count: userStats.total_jobs, route: '/jobs', icon: <FaBriefcase />, color: '#10b981', trend: '4%', up: true },
    { title: 'Applications', count: userStats.total_applications, route: '/applications', icon: <FaClipboardList />, color: '#f59e0b', trend: '2%', up: false },
    { title: 'Submissions', count: userStats.total_submissions, route: '/submissions', icon: <FaFileAlt />, color: '#8b5cf6', trend: '8%', up: true },
  ];

  return (
    <div className="dashboard-root">
      <header className="dashboard-nav py-3 px-4 mb-4 border-bottom bg-white d-flex justify-content-between align-items-center shadow-sm sticky-top">
        <div className="d-flex align-items-center">
          <div className="system-status me-4">
            <span className="pulse-dot"></span>
            <span className="small text-muted fw-semibold">SYSTEM LIVE</span>
          </div>
          <div className="divider-vr me-4"></div>
          <h5 className="mb-0 fw-bold text-dark">Platform Analytics</h5>
        </div>
        <div className="d-flex align-items-center gap-3">
          <Badge bg="light" text="dark" className="border px-3 py-2 fw-medium rounded-pill">
            <FaClock className="me-2 text-primary" />
            {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </Badge>
          <div className="admin-pill shadow-sm">
            <img src="https://ui-avatars.com/api/?name=Admin&background=6366f1&color=fff" alt="admin" />
            <div className="ms-2 d-none d-md-block">
              <p className="mb-0 small fw-bold">Admin Portal</p>
              <p className="mb-0 x-small text-muted">SuperAdmin</p>
            </div>
          </div>
        </div>
      </header>

      <Container fluid className="px-4 pb-5">
        {/* Welcome Section */}
        <div className="mb-4">
          <h2 className="fw-bold text-dark mb-1">Overview Dashboard</h2>
          <p className="text-muted">Current platform metrics and real-time user engagement.</p>
        </div>

        {/* Analytic Stats */}
        <Row className="g-4 mb-4">
          {cards.map((card, index) => (
            <Col key={index} xl={3} md={6}>
              <Card className="analytic-card h-100 border-0 shadow-sm" onClick={() => navigate(card.route)}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-uppercase small fw-bold text-muted tracking-wide">{card.title}</span>
                    <div className="icon-badge" style={{ color: card.color, background: `${card.color}15` }}>
                      {card.icon}
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between">
                    <div>
                      <h2 className="fw-bold mb-0">{card.count.toLocaleString()}</h2>
                      <div className={`trend-text mt-1 ${card.up ? 'text-success' : 'text-danger'}`}>
                        {card.up ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />} {card.trend} <span className="text-muted fw-normal x-small ms-1">than last month</span>
                      </div>
                    </div>
                    <div className="spark-box">
                      <Sparkline color={card.color} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="g-4">
          {/* Main Chart/Goal Section */}
          <Col lg={8}>
            <Card className="border-0 shadow-sm rounded-3 mb-4 h-100">
              <Card.Header className="bg-white border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-0">Daily Engagement Goals</h5>
                <FaSyncAlt className="text-muted x-small cursor-pointer rotate-hover" onClick={fetchSummary} />
              </Card.Header>
              <Card.Body className="p-4">
                <div className="goal-grid">
                  <div className="goal-item p-4 rounded-3 border bg-light-subtle shadow-xs">
                    <div className="d-flex justify-content-between mb-3">
                      <span className="fw-bold">User Growth</span>
                      <span className="text-primary fw-bold">{summary.newUsers}</span>
                    </div>
                    <ProgressBar now={Math.min(100, summary.newUsers * 5)} variant="primary" className="goal-progress" />
                    <p className="mt-2 mb-0 x-small text-muted text-end">Goal: 20 per day</p>
                  </div>
                  <div className="goal-item p-4 rounded-3 border bg-light-subtle shadow-xs">
                    <div className="d-flex justify-content-between mb-3">
                      <span className="fw-bold">Applications</span>
                      <span className="text-success fw-bold">{summary.jobApplications}</span>
                    </div>
                    <ProgressBar now={Math.min(100, summary.jobApplications * 10)} variant="success" className="goal-progress" />
                    <p className="mt-2 mb-0 x-small text-muted text-end">Goal: 10 per day</p>
                  </div>
                  <div className="goal-item p-4 rounded-3 border bg-light-subtle shadow-xs">
                    <div className="d-flex justify-content-between mb-3">
                      <span className="fw-bold">Submissions</span>
                      <span className="text-warning fw-bold">{summary.placementSubmissions}</span>
                    </div>
                    <ProgressBar now={Math.min(100, summary.placementSubmissions * 20)} variant="warning" className="goal-progress" />
                    <p className="mt-2 mb-0 x-small text-muted text-end">Goal: 5 per day</p>
                  </div>
                </div>

                <div className="mt-5 pt-4 border-top">
                  <h6 className="fw-bold mb-3 d-flex align-items-center">
                    <FaChartArea className="me-2 text-primary" />
                    Live System Capacity
                  </h6>
                  <div className="d-flex gap-4">
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between mb-1 x-small fw-bold text-muted">
                        <span>CPU USAGE</span>
                        <span>24%</span>
                      </div>
                      <ProgressBar now={24} variant="dark" style={{ height: '4px' }} />
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between mb-1 x-small fw-bold text-muted">
                        <span>API LATENCY</span>
                        <span>12ms</span>
                      </div>
                      <ProgressBar now={12} variant="info" style={{ height: '4px' }} />
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Activity Feed Sidebar */}
          <Col lg={4}>
            <Card className="border-0 shadow-sm rounded-3 h-100">
              <Card.Header className="bg-white border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-0">Recent Activity</h5>
                <FaEllipsisV className="text-muted x-small" />
              </Card.Header>
              <Card.Body className="p-4">
                <div className="feed-list">
                  {activities.length > 0 ? (
                    activities.slice(0, 7).map((activity, index) => (
                      <div key={index} className="feed-item d-flex mb-4">
                        <div className="feed-icon-box" style={{ background: index % 2 === 0 ? '#eff6ff' : '#ecfdf5' }}>
                          <div className="feed-dot" style={{ background: index % 2 === 0 ? '#3b82f6' : '#10b981' }}></div>
                        </div>
                        <div className="ms-3 flex-grow-1 overflow-hidden">
                          <div className="d-flex justify-content-between align-items-start">
                            <span className="small fw-bold text-dark text-truncate">{activity.user_name}</span>
                            <span className="x-small text-muted flex-shrink-0 ms-2">
                              {new Date(activity.activity_date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <p className="x-small text-muted mb-0">{activity.activity_type}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-5">
                      <p className="text-muted x-small">Listening for system hooks...</p>
                    </div>
                  )}
                </div>
                <div className="d-grid mt-3">
                  <button className="btn btn-outline-light text-primary btn-sm border-0 fw-bold x-small">VIEW ALL LOGS</button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <style>{`
        .dashboard-root {
          background-color: #f1f5f9;
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
        }

        .dashboard-nav {
          z-index: 1020;
        }

        .divider-vr {
          width: 1px;
          height: 30px;
          background: #e2e8f0;
        }

        .system-status {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .pulse-dot {
          width: 8px;
          height: 8px;
          background-color: #10b981;
          border-radius: 50%;
          box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
          70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }

        .admin-pill {
          display: flex;
          align-items: center;
          background: white;
          padding: 4px 12px 4px 4px;
          border-radius: 50px;
          border: 1px solid #e2e8f0;
        }

        .admin-pill img {
          width: 32px;
          height: 32px;
          border-radius: 50%;
        }

        .analytic-card {
          cursor: pointer;
          transition: all 0.2s ease;
          border-radius: 12px !important;
        }

        .analytic-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1) !important;
        }

        .icon-badge {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          font-size: 1.1rem;
        }

        .spark-box {
          width: 120px;
        }

        .trend-text {
          font-size: 0.75rem;
          font-weight: 700;
        }

        .tracking-wide {
          letter-spacing: 0.05em;
        }

        .x-small {
          font-size: 0.7rem;
        }

        .shadow-xs {
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        }

        .goal-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
        }

        .goal-progress {
          height: 8px;
          border-radius: 10px;
          background-color: #f1f5f9;
        }

        /* Feed Styling */
        .feed-icon-box {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .feed-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }

        .feed-item {
          position: relative;
        }

        .feed-item:not(:last-child)::after {
          content: '';
          position: absolute;
          left: 15px;
          top: 32px;
          bottom: -24px;
          width: 2px;
          background: #f1f5f9;
        }

        .rotate-hover {
          transition: transform 0.4s ease;
        }

        .rotate-hover:hover {
          transform: rotate(180deg);
        }

        @media (max-width: 576px) {
          .goal-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
